import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import { selectGameConfig } from "src/store/common"
import { zoneNames, zonesFor } from "src/utils/battlefield"
import { units as unitImages } from "src/assets/images"

import ModalService from "src/modals"

import Modal from "src/components/modals/Modal"
import { foodIcon } from "src/utils/resources"

import "./UnitModal.css"

/* One unit, in full: portrait, stats, where it may stand on the battle board, and what it does
   that the numbers alone do not say. Opened by clicking any unit chip, anywhere. */

// CONSUMPTION has no glyph here: what a unit eats is its own side's food, so it takes `foodIcon`
// of the side the unit belongs to — 🌽 for a soldier, 🥩 for a walker.
const STAT_ROWS = [
  ["ATTACK", "⚔"],
  ["DEFENSE", "🛡"],
  ["HEALTH", "❤"],
  ["SPEED", "🏃"],
  ["CONSUMPTION", null],
]

const UnitModal = ({ unitType }) => {
  const { t } = useTranslation()
  const gameConfig = useSelector(selectGameConfig)

  // The two rosters never share a name, so the unit says which side it belongs to.
  const team = gameConfig?.human_config?.TROOPS?.[unitType] ? "human" : "zombie"
  const stats = gameConfig?.[`${team}_config`]?.TROOPS?.[unitType]
  if (!stats) return null

  const cost = stats.COST_IN_WORKERS ?? stats.COST
  // Where it may stand is the owner's table, not the role: the role said "back" for a medic that
  // stands on the front, last (P6, amended 2026-09-19), and nothing about a titan the owner sends
  // to the corners (Z1).
  const zones = zonesFor(gameConfig, team, unitType)
  const ability = t(`game.units.abilities.${unitType}`, { defaultValue: "" })
  const name = t(`city.units.${unitType}`, { defaultValue: unitType })
  const side = t(`game.battle.${team}`)

  // The portrait is a keyhole onto a 1189×1323 plate; pressing it opens the showcase
  // (owner's ask, 2026-09-06), which draws the same file at the height of the screen.
  const openShowcase = () =>
    ModalService.show("ArtShowcaseModal", {
      src: unitImages[unitType],
      title: name,
      kicker: `${t("showcase.unit")} · ${side}`,
      subtitle: zoneNames(t, gameConfig, team, unitType),
      lines: [ability],
      team,
    })

  return (
    <Modal width="min(720px, 92%)" isolated={false} scope="map" top="8vh" maxHeight="84vh">
      <div className="unit-modal">
        <header className="unit-modal-head">
          <button type="button" className="art-zoomable unit-modal-art-button" onClick={openShowcase} title={t("showcase.open")}>
            <img src={unitImages[unitType]} alt={t("showcase.open")} className="unit-modal-art" />
          </button>
          <div>
            <h2>{name}</h2>
            <p className={`unit-modal-team ${team}`}>
              {side}
            </p>
            <p className="unit-modal-zones">
              {t("game.units.standsIn")}{" "}
              {zones.map((zone) => (
                <span className={`unit-modal-zone zone-${zone}`} key={zone}>
                  {t(`game.battle.zones.${zone}`)}
                </span>
              ))}
            </p>
          </div>
        </header>

        <div className="unit-modal-stats">
          {STAT_ROWS.filter(([key]) => stats[key] != null).map(([key, icon]) => (
            // Every stat is a door to its combat rule: press it and a helper says what the
            // number does on the board (2026-08-21).
            <button
              type="button"
              className="unit-modal-stat clickable"
              key={key}
              onClick={() => ModalService.show("StatHelpModal", { stat: key, team })}
              title={t("game.statHelp.open")}
            >
              <span className="unit-modal-stat-label">
                {icon || foodIcon(team)} {t(`game.units.stats.${key}`)}
              </span>
              <span className="unit-modal-stat-value">{stats[key]}</span>
            </button>
          ))}
          {cost != null && (
            <div className="unit-modal-stat">
              <span className="unit-modal-stat-label">💰 {t(`game.units.stats.${team === "human" ? "COST_IN_WORKERS" : "COST"}`)}</span>
              <span className="unit-modal-stat-value">{cost}</span>
            </div>
          )}
        </div>

        {ability && <p className="unit-modal-ability">✨ {ability}</p>}
      </div>
    </Modal>
  )
}

export default UnitModal
